import type { RequestContext } from "../context/request-context.js";
import { getActiveClientId } from "../../repositories/tenant-guard.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { domainId, type InspectionBookId, type InspectionId, type EquipmentId, type ValidationIssueId, type EngineerDecisionId, type ClientId } from "../../shared/kernel/identifiers.js";
import type { 
  InspectionBook, 
  InspectionBookItem, 
  BookValidationSummary
} from "../../domain/inspection-book/inspection-book.js";
import { DEFAULT_BOOK_TEMPLATE_ITEMS } from "../../domain/inspection-book/template.js";
import type { ValidationIssue } from "../../domain/inspection-book/validation-issue.js";
import type { EngineerDecision, ApprovedEngineeringPattern } from "../../domain/inspection-book/engineer-decision.js";
import { technicalValidationEngine } from "../../domain/inspection-book/technical-validation-engine.js";
import { inspectionBookReleaseGate } from "../../domain/inspection-book/release-gate.js";
import type { InspectionBookRepository, InspectionRepository, IntegrityReportRepository, ChecklistRepository, RecommendationRepository } from "../../repositories/operational-repositories.js";
import type { EquipmentRepository } from "../../equipment/equipment-domain.js";
import { buildGeneratedLineList, type GeneratedLineList } from "../../domain/inspection-book/line-list.js";
import { documentEngine } from "../../infrastructure/documents/document-engine.js";

export interface InspectionBookServiceDependencies {
  readonly bookRepo: InspectionBookRepository;
  readonly inspectionRepo?: InspectionRepository;
  readonly reportRepo?: IntegrityReportRepository;
  readonly checklistRepo?: ChecklistRepository;
  readonly recommendationRepo?: RecommendationRepository;
  readonly equipmentRepo?: EquipmentRepository;
}

export class InspectionBookService {
  constructor(private deps: InspectionBookServiceDependencies) {}

  /**
   * STUDIO INSPECT IA — o Book não guarda modelo de documento congelado como
   * ME/LP/PM (o consolidador remonta a cada impressão); por isso a versão
   * visual é CARIMBADA aqui, na emissão, e o consolidador imprime sempre pela
   * versão carimbada (mesmo padrão do RI). Sem a ligação, nada muda.
   */
  private visualStamp: ((bookId: string) => Promise<unknown>) | null = null;

  attachVisualStamp(stamp: (bookId: string) => Promise<unknown>): void {
    this.visualStamp = stamp;
  }

  /**
   * Leitura pura (nunca cria) — usada pelo Book de Inspeção (consolidador
   * documental) para ler o PIT já elaborado no Prontuário Técnico/NR-13 sem
   * disparar a criação de um dossiê novo, que é o que `getOrCreateBook` faz.
   */
  async findBook(context: RequestContext, inspectionId: InspectionId): Promise<InspectionBook | null> {
    return this.deps.bookRepo.findByInspectionId(context, inspectionId);
  }

  async getOrCreateBook(
    context: RequestContext,
    inspectionId: InspectionId,
    equipmentId: EquipmentId
  ): Promise<InspectionBook> {
    const clientId = getActiveClientId(context);
    let book = await this.deps.bookRepo.findByInspectionId(context, inspectionId);

    if (!book) {
      const bookId = domainId<InspectionBookId>(crypto.randomUUID());
      const now = new Date().toISOString();

      const items: InspectionBookItem[] = DEFAULT_BOOK_TEMPLATE_ITEMS.map((tmpl, idx) => ({
        id: crypto.randomUUID(),
        clientId: clientId as ClientId,
        inspectionBookId: bookId,
        inspectionId,
        documentType: tmpl.documentType,
        title: tmpl.defaultTitle,
        category: tmpl.category,
        order: tmpl.order,
        applicability: 'APPLICABLE',
        status: tmpl.isSystemGenerated ? 'AVAILABLE' : (tmpl.defaultRequired ? 'PENDING' : 'NOT_EVALUATED'),
        source: tmpl.isSystemGenerated ? 'SYSTEM_GENERATED' : 'UPLOADED',
        revision: '00',
        createdAt: now,
        updatedAt: now,
      }));

      const initialSummary: BookValidationSummary = {
        totalItems: items.length,
        compliantItems: 0,
        pendingItems: items.filter(i => i.status === 'PENDING').length,
        notApplicableItems: 0,
        blockersCount: 0,
        criticalCount: 0,
        highCount: 0,
        mediumCount: 0,
        lowCount: 0,
        cortexSuggestionsCount: 0,
        resolvedCount: 0,
        overrideCount: 0,
      };

      book = {
        id: bookId,
        clientId: clientId as ClientId,
        inspectionId,
        equipmentId,
        bookNumber: `BOOK-${new Date().getFullYear()}-${inspectionId.slice(0, 8).toUpperCase()}`,
        title: `Book de Inspeção Técnica e Integridade`,
        revision: '00',
        status: 'IN_PROGRESS',
        items,
        validationSummary: initialSummary,
        createdAt: now,
        updatedAt: now,
      };

      await this.deps.bookRepo.save(context, book);
    }

    return book;
  }

  async setNotApplicable(
    context: RequestContext,
    bookId: InspectionBookId,
    itemId: string,
    reason: string,
    professionalId?: string
  ): Promise<InspectionBook> {
    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, 'Justificativa técnica de Não Aplicabilidade é obrigatória.');
    }

    const book = await this.deps.bookRepo.findById(context, bookId);
    if (!book) throw new AppError(ErrorCode.NotFound, 'Book de Inspeção não encontrado.');

    if (book.status === 'ISSUED') {
      throw new AppError(ErrorCode.Validation, 'Book de Inspeção já emitido é imutável.');
    }

    const now = new Date().toISOString();
    const updatedItems = book.items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          applicability: 'NOT_APPLICABLE' as const,
          notApplicableReason: reason.trim(),
          notApplicableBy: professionalId || 'Usuário Responsável',
          notApplicableAt: now,
          status: 'NOT_APPLICABLE' as const,
          updatedAt: now,
        };
      }
      return item;
    });

    const updatedBook: InspectionBook = {
      ...book,
      items: updatedItems,
      updatedAt: now,
    };

    await this.deps.bookRepo.save(context, updatedBook);
    return updatedBook;
  }

  async setApplicable(
    context: RequestContext,
    bookId: InspectionBookId,
    itemId: string
  ): Promise<InspectionBook> {
    const book = await this.deps.bookRepo.findById(context, bookId);
    if (!book) throw new AppError(ErrorCode.NotFound, 'Book de Inspeção não encontrado.');

    if (book.status === 'ISSUED') {
      throw new AppError(ErrorCode.Validation, 'Book de Inspeção já emitido é imutável.');
    }

    const now = new Date().toISOString();
    const updatedItems = book.items.map(item => {
      if (item.id === itemId) {
        const { notApplicableReason, notApplicableBy, notApplicableAt, ...rest } = item;
        return {
          ...rest,
          applicability: 'APPLICABLE' as const,
          status: item.source === 'SYSTEM_GENERATED' ? 'AVAILABLE' as const : 'PENDING' as const,
          updatedAt: now,
        };
      }
      return item;
    });

    const updatedBook: InspectionBook = {
      ...book,
      items: updatedItems,
      updatedAt: now,
    };

    await this.deps.bookRepo.save(context, updatedBook);
    return updatedBook;
  }

  async attachDocument(
    context: RequestContext,
    bookId: InspectionBookId,
    itemId: string,
    fileData: { fileName: string; fileUrl: string; sizeBytes?: number; revision?: string; notes?: string }
  ): Promise<InspectionBook> {
    const book = await this.deps.bookRepo.findById(context, bookId);
    if (!book) throw new AppError(ErrorCode.NotFound, 'Book de Inspeção não encontrado.');

    if (book.status === 'ISSUED') {
      throw new AppError(ErrorCode.Validation, 'Book de Inspeção já emitido é imutável.');
    }

    const now = new Date().toISOString();
    const updatedItems = book.items.map(item => {
      if (item.id === itemId) {
        const { notes, ...rest } = item;
        const finalNotes = fileData.notes || item.notes;
        return {
          ...rest,
          fileName: fileData.fileName,
          fileUrl: fileData.fileUrl,
          fileSizeBytes: fileData.sizeBytes || 1024 * 100,
          revision: fileData.revision || item.revision,
          ...(finalNotes ? { notes: finalNotes } : {}),
          status: 'AVAILABLE' as const,
          validationStatus: 'VALID' as const,
          updatedAt: now,
        };
      }
      return item;
    });

    const updatedBook: InspectionBook = {
      ...book,
      items: updatedItems,
      updatedAt: now,
    };

    await this.deps.bookRepo.save(context, updatedBook);
    return updatedBook;
  }

  async executeBookValidation(
    context: RequestContext,
    inspectionId: InspectionId
  ): Promise<{ book: InspectionBook; issues: ValidationIssue[]; summary: BookValidationSummary; result: string }> {
    const clientId = getActiveClientId(context) as ClientId;
    const book = await this.deps.bookRepo.findByInspectionId(context, inspectionId);
    if (!book) throw new AppError(ErrorCode.NotFound, 'Book de Inspeção não encontrado.');

    // 1. Gather all inspection context data
    const inspection = this.deps.inspectionRepo ? await this.deps.inspectionRepo.findById(context, inspectionId) : null;
    const equipment = (this.deps.equipmentRepo && book.equipmentId) ? await this.deps.equipmentRepo.getEquipment(context.activeClientId as ClientId, book.equipmentId) : null;
    const report = this.deps.reportRepo ? await this.deps.reportRepo.findByInspection(context, inspectionId) : null;
    const checklist = this.deps.checklistRepo ? await this.deps.checklistRepo.findByInspection(context, inspectionId) : null;
    const recommendations = (this.deps.recommendationRepo && report) ? await this.deps.recommendationRepo.findByIntegrityReport(context, report.id) : [];

    // 2. Deterministic Rule Engine Validation
    const deterministicIssues = technicalValidationEngine.validate({
      book,
      inspection,
      equipment,
      report,
      checklist,
      recommendations,
    });

    // 3. Referências documentais citadas no laudo e ausentes da matriz.
    // Regras DETERMINÍSTICAS (antes eram respostas do MockProvider do Córtex,
    // que ainda acrescentava uma "confirmação de consistência" que ninguém
    // verificou). Nada aqui é IA: a revisão de texto é do INSPECT IA REVIEW.
    const cortexIssues: ValidationIssue[] = documentReferenceIssues(book, report, clientId, inspectionId);

    const allIssues = [...deterministicIssues, ...cortexIssues];

    // Persist all generated issues
    for (const iss of allIssues) {
      await this.deps.bookRepo.saveValidationIssue(context, iss);
    }

    // 4. Calculate Summary and Release Gate Evaluation
    const gateEval = inspectionBookReleaseGate.evaluate(book, allIssues);

    const now = new Date().toISOString();
    const summary: BookValidationSummary = {
      totalItems: book.items.length,
      compliantItems: book.items.filter(i => (i.applicability === 'APPLICABLE' && i.status === 'AVAILABLE') || (i.applicability === 'NOT_APPLICABLE' && i.notApplicableReason)).length,
      pendingItems: book.items.filter(i => i.applicability === 'APPLICABLE' && i.status === 'PENDING').length,
      notApplicableItems: book.items.filter(i => i.applicability === 'NOT_APPLICABLE').length,
      blockersCount: allIssues.filter(i => i.severity === 'BLOCKER' && i.status === 'OPEN').length,
      criticalCount: allIssues.filter(i => i.severity === 'CRITICAL' && i.status === 'OPEN').length,
      highCount: allIssues.filter(i => i.severity === 'HIGH' && i.status === 'OPEN').length,
      mediumCount: allIssues.filter(i => i.severity === 'MEDIUM' && i.status === 'OPEN').length,
      lowCount: allIssues.filter(i => i.severity === 'LOW' && i.status === 'OPEN').length,
      cortexSuggestionsCount: cortexIssues.length,
      resolvedCount: allIssues.filter(i => i.status === 'RESOLVED').length,
      overrideCount: allIssues.filter(i => i.status === 'ENGINEER_OVERRIDE').length,
      lastValidatedAt: now,
      lastValidatedBy: 'Validation Engine (regras documentais)',
    };

    let newStatus = book.status;
    if (newStatus !== 'ISSUED') {
      if (gateEval.result === 'BLOCKED') newStatus = 'BLOCKED';
      else if (gateEval.result === 'RELEASED_WITH_ENGINEER_OVERRIDE') newStatus = 'READY_WITH_ENGINEER_OVERRIDE';
      else if (gateEval.result === 'RELEASED') newStatus = 'READY_FOR_ISSUE';
    }

    const updatedBook: InspectionBook = {
      ...book,
      status: newStatus,
      validationSummary: summary,
      updatedAt: now,
    };

    await this.deps.bookRepo.save(context, updatedBook);

    return {
      book: updatedBook,
      issues: allIssues,
      summary,
      result: gateEval.result,
    };
  }

  async overrideIssue(
    context: RequestContext,
    inspectionId: InspectionId,
    issueId: ValidationIssueId,
    reason: string,
    engineerProfessionalId: string,
    userId: string
  ): Promise<ValidationIssue> {
    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, 'Justificativa do Engenheiro é estritamente obrigatória para liberação de pendência.');
    }

    const issues = await this.deps.bookRepo.listValidationIssues(context, inspectionId);
    const target = issues.find(i => i.id === issueId);
    if (!target) throw new AppError(ErrorCode.NotFound, 'Pendência de validação não encontrada.');

    if (!target.overrideAllowed) {
      throw new AppError(ErrorCode.Validation, 'Esta regra de segurança e integridade não permite liberação por override.');
    }

    const now = new Date().toISOString();
    const decisionId = domainId<EngineerDecisionId>(crypto.randomUUID());

    const updatedIssue: ValidationIssue = {
      ...target,
      status: 'ENGINEER_OVERRIDE',
      overrideAt: now,
      overrideBy: engineerProfessionalId,
      overrideReason: reason.trim(),
      engineerDecisionId: decisionId,
      updatedAt: now,
    };

    await this.deps.bookRepo.saveValidationIssue(context, updatedIssue);

    // Save formal EngineerDecision audit trail
    const decision: EngineerDecision = {
      id: decisionId,
      clientId: getActiveClientId(context) as ClientId,
      inspectionId,
      validationIssueId: issueId,
      decision: 'OVERRIDDEN',
      reason: reason.trim(),
      engineerProfessionalId,
      userId,
      createdAt: now,
    };
    await this.deps.bookRepo.saveEngineerDecision(context, decision);

    return updatedIssue;
  }

  async resolveIssue(
    context: RequestContext,
    inspectionId: InspectionId,
    issueId: ValidationIssueId,
    resolution: string,
    resolvedBy?: string
  ): Promise<ValidationIssue> {
    const issues = await this.deps.bookRepo.listValidationIssues(context, inspectionId);
    const target = issues.find(i => i.id === issueId);
    if (!target) throw new AppError(ErrorCode.NotFound, 'Pendência de validação não encontrada.');

    const now = new Date().toISOString();
    const updatedIssue: ValidationIssue = {
      ...target,
      status: 'RESOLVED',
      resolvedAt: now,
      resolvedBy: resolvedBy || 'Engenheiro / Inspetor',
      resolution: resolution.trim() || 'Corrigido e validado.',
      updatedAt: now,
    };

    await this.deps.bookRepo.saveValidationIssue(context, updatedIssue);
    return updatedIssue;
  }

  async recordEngineerDecision(
    context: RequestContext,
    decisionInput: Omit<EngineerDecision, 'id' | 'createdAt' | 'clientId'>
  ): Promise<EngineerDecision> {
    const clientId = getActiveClientId(context) as ClientId;
    const now = new Date().toISOString();
    const id = domainId<EngineerDecisionId>(crypto.randomUUID());

    const decision: EngineerDecision = {
      ...decisionInput,
      id,
      clientId,
      createdAt: now,
    };

    await this.deps.bookRepo.saveEngineerDecision(context, decision);

    // If accepted or edited, update ApprovedEngineeringPattern for client
    if (decision.decision === 'ACCEPTED' || decision.decision === 'EDITED_AND_ACCEPTED') {
      const patternText = decision.finalEngineerContent || decision.aiSuggestion;
      if (patternText) {
        const pattern: ApprovedEngineeringPattern = {
          id: crypto.randomUUID(),
          clientId,
          problemType: 'REPORT_OR_RECOMMENDATION_REVISION',
          contextTag: decision.recommendationId ? 'RECOMMENDATION' : 'INTEGRITY_REPORT',
          patternText,
          frequency: 1,
          lastUsedAt: now,
          confidenceLevel: 'HIGH',
        };
        await this.deps.bookRepo.saveApprovedPattern(context, pattern);
      }
    }

    return decision;
  }

  async issueBook(
    context: RequestContext,
    bookId: InspectionBookId,
    userName: string,
    isEngineer: boolean = true
  ): Promise<InspectionBook> {
    const book = await this.deps.bookRepo.findById(context, bookId);
    if (!book) throw new AppError(ErrorCode.NotFound, 'Book de Inspeção não encontrado.');

    if (book.status === 'ISSUED') {
      return book; // Already issued and immutable
    }

    const issues = await this.deps.bookRepo.listValidationIssues(context, book.inspectionId);

    // Release gate verification in application layer
    inspectionBookReleaseGate.assertCanIssue(book, issues, isEngineer, true);

    const now = new Date().toISOString();
    // A Lista de Linhas desta revisão é congelada aqui: TAGs, dados
    // apresentados, ordem, total e referências. Linha cadastrada ou alterada
    // depois só aparece em uma NOVA revisão.
    const equipmentOfBook = (this.deps.equipmentRepo && book.equipmentId)
      ? await this.deps.equipmentRepo.getEquipment(context.activeClientId as ClientId, book.equipmentId).catch(() => null)
      : null;
    const generatedLineList = await this.buildLineListOf(context, equipmentOfBook);
    const snapshotJson = JSON.stringify({
      bookId: book.id,
      inspectionId: book.inspectionId,
      equipmentId: book.equipmentId,
      bookNumber: book.bookNumber,
      revision: book.revision,
      items: book.items,
      validationIssues: issues,
      generatedLineList,
      issuedBy: userName,
      issuedAt: now,
    });

    const issuedBook: InspectionBook = {
      ...book,
      status: 'ISSUED',
      issuedAt: now,
      issuedBy: userName,
      snapshotJson,
      updatedAt: now,
    };

    await this.deps.bookRepo.save(context, issuedBook);
    // O carimbo é aparência: uma falha aqui nunca impede a emissão do Book.
    await this.visualStamp?.(String(issuedBook.id)).catch(() => undefined);
    return issuedBook;
  }

  async listIssues(context: RequestContext, inspectionId: InspectionId): Promise<readonly ValidationIssue[]> {
    return this.deps.bookRepo.listValidationIssues(context, inspectionId);
  }

  async listEngineerDecisions(context: RequestContext, inspectionId?: InspectionId): Promise<readonly EngineerDecision[]> {
    return this.deps.bookRepo.listEngineerDecisions(context, inspectionId);
  }

  async listApprovedPatterns(context: RequestContext): Promise<readonly ApprovedEngineeringPattern[]> {
    return this.deps.bookRepo.listApprovedPatterns(context);
  }

  /**
   * LISTA DE LINHAS do Book: quando o Equipamento do Book é um SISTEMA DE
   * TUBULAÇÃO, é gerada do cadastro oficial (Equipment.pipingSystemId). Se o
   * Book já foi EMITIDO, devolve o que ficou congelado no snapshot daquela
   * revisão — cadastrar ou alterar Linha depois nunca muda Book histórico.
   */
  private async resolveGeneratedLineList(context: RequestContext, book: InspectionBook, equipment: any): Promise<GeneratedLineList | null> {
    if (book.snapshotJson) {
      try {
        const frozen = JSON.parse(book.snapshotJson) as { generatedLineList?: GeneratedLineList | null };
        if (frozen.generatedLineList) return frozen.generatedLineList;
      } catch { /* snapshot ilegível: cai para a geração corrente */ }
    }
    return this.buildLineListOf(context, equipment);
  }

  private async buildLineListOf(context: RequestContext, equipment: any): Promise<GeneratedLineList | null> {
    if (!equipment || equipment.equipmentType !== "PIPING_SYSTEM" || !this.deps.equipmentRepo) return null;
    const equipments = await this.deps.equipmentRepo.listEquipments(equipment.clientId as ClientId).catch(() => []);
    return buildGeneratedLineList(equipment, equipments);
  }

  async generateConsolidatedPdf(context: RequestContext, bookId: InspectionBookId): Promise<string> {
    const book = await this.deps.bookRepo.findById(context, bookId);
    if (!book) throw new AppError(ErrorCode.NotFound, 'Book de Inspeção não encontrado.');

    const inspection = this.deps.inspectionRepo ? await this.deps.inspectionRepo.findById(context, book.inspectionId) : null;
    const equipment = (this.deps.equipmentRepo && book.equipmentId) ? await this.deps.equipmentRepo.getEquipment(context.activeClientId as ClientId, book.equipmentId) : null;
    const report = this.deps.reportRepo ? await this.deps.reportRepo.findByInspection(context, book.inspectionId) : null;
    const checklist = this.deps.checklistRepo ? await this.deps.checklistRepo.findByInspection(context, book.inspectionId) : null;
    const recommendations = (this.deps.recommendationRepo && report) ? await this.deps.recommendationRepo.findByIntegrityReport(context, report.id) : [];
    const issues = await this.deps.bookRepo.listValidationIssues(context, book.inspectionId);

    const snapshot = {
      id: book.bookNumber,
      type: 'INSPECTION_BOOK',
      revision: book.revision,
      clientId: (book as any).clientId ?? inspection?.clientId,
      data: {
        book,
        inspection,
        equipment,
        report,
        checklist,
        recommendations,
        issues,
        generatedLineList: await this.resolveGeneratedLineList(context, book, equipment),
      },
      createdAt: new Date(),
    };

    return documentEngine.generatePdf(snapshot);
  }

  async downloadConsolidatedPdf(context: RequestContext, bookId: InspectionBookId, filename?: string): Promise<Uint8Array> {
    const book = await this.deps.bookRepo.findById(context, bookId);
    if (!book) throw new AppError(ErrorCode.NotFound, 'Book de Inspeção não encontrado.');

    const inspection = this.deps.inspectionRepo ? await this.deps.inspectionRepo.findById(context, book.inspectionId) : null;
    const equipment = (this.deps.equipmentRepo && book.equipmentId) ? await this.deps.equipmentRepo.getEquipment(context.activeClientId as ClientId, book.equipmentId) : null;
    const report = this.deps.reportRepo ? await this.deps.reportRepo.findByInspection(context, book.inspectionId) : null;
    const checklist = this.deps.checklistRepo ? await this.deps.checklistRepo.findByInspection(context, book.inspectionId) : null;
    const recommendations = (this.deps.recommendationRepo && report) ? await this.deps.recommendationRepo.findByIntegrityReport(context, report.id) : [];
    const issues = await this.deps.bookRepo.listValidationIssues(context, book.inspectionId);

    const snapshot = {
      id: book.bookNumber,
      type: 'INSPECTION_BOOK',
      revision: book.revision,
      data: {
        book,
        inspection,
        equipment,
        report,
        checklist,
        recommendations,
        issues,
        generatedLineList: await this.resolveGeneratedLineList(context, book, equipment),
      },
      createdAt: new Date(),
    };

    const targetName = filename || `${book.bookNumber}_Rev${book.revision}.pdf`;
    return documentEngine.downloadPdf(snapshot, targetName);
  }
}

/**
 * O laudo cita B-Scan ou croqui/desenho e a matriz do Book não tem o anexo
 * correspondente. Mesmo critério que o Córtex aplicava por palavra-chave,
 * agora declarado como regra, com origem RULE_ENGINE.
 */
function documentReferenceIssues(book: InspectionBook, report: unknown, clientId: ClientId, inspectionId: InspectionId): ValidationIssue[] {
  const text = JSON.stringify(report ?? {}).toLowerCase();
  const items = (book.items || []) as unknown as Array<Record<string, unknown>>;
  const attached = (types: readonly string[]) => items.some(i => types.includes(String(i["documentType"])) && i["status"] !== "NOT_APPLICABLE" && (i["attachmentId"] || i["fileName"] || i["fileUrl"]));
  const now = new Date().toISOString();
  const issue = (category: string, severity: string, section: string, evidence: string, action: string): ValidationIssue => ({
    id: domainId<ValidationIssueId>(crypto.randomUUID()), clientId, inspectionId, bookId: book.id,
    category, severity, source: "RULE_ENGINE", title: `Referência documental: ${section}`, description: evidence, evidence,
    suggestedAction: action, section, confidence: "HIGH", detectedBy: "Validation Engine (regra documental)", status: "OPEN",
    overrideAllowed: true, createdAt: now, updatedAt: now
  } as unknown as ValidationIssue);
  const out: ValidationIssue[] = [];
  if ((text.includes("b-scan") || text.includes("bscan")) && !attached(["BSCAN_REPORT"])) {
    out.push(issue("DOCUMENTAL", "HIGH", "Relatório B-Scan", "O texto do RI menciona B-Scan, mas o relatório B-Scan não está anexado nem referenciado na matriz do Book.", "Anexar o relatório B-Scan ou retificar o texto do RI."));
  }
  if ((text.includes("croqui") || text.includes("desenho")) && !attached(["DRAWINGS", "SKETCH"])) {
    out.push(issue("RASTREABILIDADE", "MEDIUM", "Croquis / Desenhos", "O laudo cita croqui/desenho, mas nenhum arquivo correspondente foi anexado na matriz documental.", "Anexar o croqui no item correspondente do Book."));
  }
  return out;
}
